// Channel notification + delivery-status helpers for the manager.
//
// Low-level "send a notification / report delivery status" primitives used by
// the scheduled-output orchestrator (./scheduled-output.ts) and by the kanban
// done-card path in manager-worker. Depend only on the channel senders
// (../channels/*), the runtime log leaf, and the API client — no scheduler or
// lifecycle module — so they sit in the delivery leaf layer. See ADR-0011.

import { api } from '../../api-client.js';
import { log } from '../runtime.js';
import { agentChannelTokens } from '../channels/state.js';
import { sendSlackChannelMessage } from '../channels/slack.js';
import { telegramApiCall } from '../channels/telegram.js';

// ENG-5663: cap for the full result body carried in a done-card notification.
// /host/kanban-item returns up to an 8KB result, but the channel senders used
// by sendTaskNotification do not chunk and Telegram hard-rejects a sendMessage
// payload over 4096 chars — so we trim the body to leave headroom for the
// "✅ Task Complete — <agent>\n<title>\nResult: " prefix.
const NOTIFY_RESULT_MAX_CHARS = 3500;

/** POST dispatch status back to the API so the observability columns
 *  (last_delivery_status / last_delivery_medium / last_delivery_at /
 *  consecutive_failure_count) get updated. Best-effort — a transient API
 *  failure here shouldn't take down the scheduler. */
export async function reportDeliveryStatus(
  agentId: string,
  taskId: string | undefined,
  payload: { status: 'ok' | 'failed' | 'skipped'; medium?: 'slack' | 'telegram'; error_code?: string | null },
): Promise<void> {
  if (!taskId) return;
  try {
    await api.post('/host/schedules/delivery-status', {
      agent_id: agentId,
      task_id: taskId,
      status: payload.status,
      medium: payload.medium ?? null,
      error_code: payload.error_code ?? null,
    });
  } catch (err) {
    log(`[delivery] Failed to report delivery status for ${agentId}/${taskId}: ${(err as Error).message}`);
  }
}

export async function maybePostScheduledTaskRatingPrompt(
  agentId: string,
  taskId: string,
  channelId: string,
  messageTs: string,
): Promise<void> {
  try {
    await api.post('/host/scheduled-task/rating-prompt', {
      agent_id: agentId,
      task_id: taskId,
      channel: channelId,
      message_ts: messageTs,
    });
  } catch (err) {
    log(`[rating-prompt] Failed to post rating prompt for ${agentId}/${taskId}: ${(err as Error).message}`);
  }
}

/**
 * ENG-5663: build the "✅ Task Complete / ❌ Task Failed" notification for a
 * newly-done kanban card that carries a notify target.
 *
 * The board snapshot from `/host/my-kanban` truncates each card's `result` to
 * its first paragraph (`DONE_RESULT_MAX_CHARS`, 200 chars) — fine for a board
 * view, but sending it as the notification body delivered only the result's
 * HEADER to the configured channel (the reported symptom: a calendar summary
 * arrived as just its title line). So we fetch the full, untruncated result
 * from `/host/kanban-item` (the same source the scheduled-task delivery path
 * uses) and fall back to the truncated board value only if that fetch fails,
 * so a notification is never dropped entirely. The body is capped at
 * `NOTIFY_RESULT_MAX_CHARS` because the channel senders do not chunk.
 *
 * Exported for unit testing.
 */
export async function buildDoneCardNotification(
  agentId: string,
  codeName: string,
  displayName: string,
  item: { id: string; title: string; status: string; result?: string },
): Promise<string> {
  const isFailed = item.status === 'failed';
  let resultBody = item.result ?? '';
  try {
    const full = await api.post<{ item?: { result?: string | null } }>('/host/kanban-item', {
      agent_id: agentId,
      item_id: item.id,
    });
    if (full.item?.result) resultBody = full.item.result;
  } catch (err) {
    log(`[notify] full-result fetch failed for card ${item.id} on '${codeName}': ${(err as Error).message} — using truncated board result`);
  }
  const resultLine = resultBody
    ? `\n${isFailed ? 'Reason' : 'Result'}: ${resultBody.slice(0, NOTIFY_RESULT_MAX_CHARS)}`
    : '';
  const emoji = isFailed ? '❌' : '✅';
  return `${emoji} ${isFailed ? 'Task Failed' : 'Task Complete'} — ${displayName}\n${item.title}${resultLine}`;
}

/** Dispatch a message via the given channel.
/**
 * ENG-7643 (ADR-0044 P5a): resolve the DM target for a parked card's requester.
 * Slack DMs the sender_id (a Uxxxx user id — chat.postMessage opens the IM as the
 * agent's bot); Telegram DMs the external_id (the private-chat id). Other channels
 * have no agent-bot person-DM yet, so return null and the caller skips (graceful
 * fallback — the human can still respond from the board).
 */
export function resolveRequesterDmTarget(requester: {
  channel: string;
  sender_id: string;
  external_id: string | null;
}): { channel: 'slack' | 'telegram'; to: string } | null {
  if (requester.channel === 'slack' && requester.sender_id) {
    return { channel: 'slack', to: requester.sender_id };
  }
  if (requester.channel === 'telegram' && requester.external_id) {
    return { channel: 'telegram', to: requester.external_id };
  }
  return null;
}

/**
 * ENG-7643 (ADR-0044 P5a): build the "your agent is waiting on you" DM body sent
 * to the requester when a card parks in waiting on a human. Carries the ask, any
 * agent-offered choices (as text — the clickable buttons are P5b), and a deep
 * link to the card's Respond drawer (`/agents/<agentId>?item=<cardId>`, which the
 * webapp already opens). `medium` controls link formatting (Slack `<url|label>`
 * vs Telegram's bare-url auto-linkify). No link when the console URL is unset.
 */
export function buildWaitingHumanNotification(args: {
  displayName: string;
  agentId: string;
  itemId: string;
  title: string;
  waitingOn?: string | null;
  details?: string | null;
  choices?: Array<{ label: string; value: string }>;
  medium: 'slack' | 'telegram';
  consoleUrl: string | null;
}): string {
  // Slack mrkdwn treats &, <, > specially (an unescaped <...> can parse as a link
  // or a mention like <!channel>). Escape interpolated user text (title, name,
  // ask, details, choice labels) for the Slack medium; Telegram text-mode needs no escape.
  const esc = (s: string): string =>
    args.medium === 'slack' ? s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;') : s;

  const header = `🔔 ${esc(args.displayName)} needs your input on "${esc(args.title)}".`;
  const ask = args.waitingOn?.trim();
  const askLine = ask ? esc(ask) : null;
  const choiceLabels = (args.choices ?? [])
    .map((c) => c.label?.trim())
    .filter((l): l is string => !!l)
    .map(esc);
  const optionsLine = choiceLabels.length > 0 ? `Options: ${choiceLabels.join(' · ')}` : null;
  let linkLine: string | null = null;
  if (args.consoleUrl) {
    const base = args.consoleUrl.replace(/\/+$/, '');
    const url = `${base}/agents/${encodeURIComponent(args.agentId)}?item=${encodeURIComponent(args.itemId)}`;
    linkLine = args.medium === 'slack' ? `<${url}|Respond>` : `Respond: ${url}`;
  }

  // ENG-7676: the full decision body, so the person can answer from the DM alone.
  // But `details` is the only unbounded part, and Telegram `sendMessage` hard-caps
  // at 4096 chars (Slack chat.postMessage text is ~40k). Clamp details to whatever
  // budget the fixed lines leave so a max-sized body never drops the whole DM
  // (CodeRabbit) - the header/ask/options/deep-link always survive intact.
  const MAX = args.medium === 'telegram' ? 4096 : 39000;
  const fixed = [header, askLine, optionsLine, linkLine].filter((l): l is string => l !== null);
  const rawDetails = args.details?.trim() ? esc(args.details.trim()) : null;
  let detailsLine: string | null = null;
  if (rawDetails) {
    // Final length = sum(fixed) + detailsLen + (fixed.length) newline separators.
    const budget = MAX - fixed.reduce((n, l) => n + l.length, 0) - fixed.length - 1; // -1 reserves the ellipsis
    if (budget > 0) {
      detailsLine = rawDetails.length <= budget ? rawDetails : `${rawDetails.slice(0, budget)}…`;
    }
  }

  return [header, askLine, detailsLine, optionsLine, linkLine]
    .filter((l): l is string => l !== null)
    .join('\n');
}

/**
 *  Returns `{ ok, error_code? }` so callers can faithfully report delivery
 *  status to the observability columns rather than assuming a void call
 *  succeeded (§10 / CR #3108333175). */
export async function sendTaskNotification(
  agentCodeName: string,
  channel: string,
  to: string,
  text: string,
): Promise<{ ok: boolean; error_code?: string }> {
  const tokens = agentChannelTokens.get(agentCodeName);

  if (channel === 'slack') {
    const botToken = tokens?.slack;
    const channelId = to.replace(/^channel:/, '');
    if (!botToken) {
      log(`No Slack bot token for '${agentCodeName}' — targeted notification dropped`);
      return { ok: false, error_code: 'SLACK_NO_TOKEN' };
    }
    const sent = await sendSlackChannelMessage(agentCodeName, channelId, text);
    return sent ? { ok: true } : { ok: false, error_code: 'SLACK_SEND_FAILED' };
  }

  if (channel === 'telegram') {
    const botToken = tokens?.telegram;
    const chatId = to.replace(/^chat:/, '');
    if (!botToken) {
      log(`No Telegram bot token for '${agentCodeName}' — notification dropped`);
      return { ok: false, error_code: 'TELEGRAM_NO_TOKEN' };
    }
    const allowedChats = tokens?.telegramAllowedChats;
    if (allowedChats && allowedChats.length > 0 && !allowedChats.includes(chatId)) {
      log(`Telegram chat ${chatId} not in allowed_chat_ids for '${agentCodeName}'`);
      return { ok: false, error_code: 'TELEGRAM_CHAT_NOT_ALLOWED' };
    }
    try {
      const result = await telegramApiCall(botToken, 'sendMessage', { chat_id: chatId, text });
      if (!result.ok) {
        log(`Telegram sendMessage failed for '${agentCodeName}': ${result.description}`);
        return { ok: false, error_code: `TELEGRAM_SEND_FAILED:${result.description ?? 'unknown'}` };
      }
      log(`Telegram notification sent for '${agentCodeName}' to chat ${chatId}`);
      return { ok: true };
    } catch (err) {
      log(`Telegram API error for '${agentCodeName}': ${(err as Error).message}`);
      return { ok: false, error_code: 'TELEGRAM_EXCEPTION' };
    }
  }

  log(`Unknown notify_channel '${channel}' for '${agentCodeName}'`);
  return { ok: false, error_code: `UNKNOWN_CHANNEL:${channel}` };
}
